import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'

import { GQL_CLIENT } from '@/graphqlClient'
import { SHARED_DOCUMENTS_ROLES } from '@autoriz/shared/files'
import { AccountType } from '@autoriz/shared/accounts'
import {
  fetchProjectSharedDocuments,
  reclassifySharedDocument,
  removeSharedDocument,
} from '../services/sharedDocuments'

export const projectSharedDocumentsQueryKey = (projectId: number) =>
  ['project-shared-documents', projectId] as const

const isOffice = (accountType: string | null | undefined): boolean =>
  accountType === AccountType.ARCHITECT || accountType === AccountType.COLLABORATOR

/**
 * #883 — A dossier's shared documents, and what the viewer does to them. The
 * backend and Hasura hold every rule; this carries the gesture and says what
 * happened.
 */
export const useProjectSharedDocuments = (
  projectId: number | undefined,
  accountType?: string | null,
) => {
  const queryClient = useQueryClient()
  const queryKey = projectSharedDocumentsQueryKey(projectId ?? 0)
  const office = isOffice(accountType)

  const query = useQuery({
    queryKey,
    queryFn: () => fetchProjectSharedDocuments(GQL_CLIENT, projectId as number),
    // Never asked for a viewer who has no section: Hasura would answer nothing.
    enabled: !!projectId && SHARED_DOCUMENTS_ROLES.includes(accountType ?? ''),
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey })

  const remove = useMutation({
    mutationFn: (fileId: string) =>
      removeSharedDocument(GQL_CLIENT, projectId as number, fileId, office ? 'office' : 'outside'),
    onSuccess: async (removed) => {
      await refresh()
      if (removed) message.success('Document retiré.')
      else message.error('Ce document ne peut plus être retiré.')
    },
    onError: (error) => {
      console.error('Error removing a shared document:', error)
      message.error('Le document n’a pas pu être retiré.')
    },
  })

  const reclassify = useMutation({
    mutationFn: ({ fileId, usage }: { fileId: string; usage: string }) =>
      reclassifySharedDocument(GQL_CLIENT, projectId as number, fileId, usage),
    onSuccess: async () => {
      await refresh()
      // The document now sits in a ledger row of the documents page.
      await queryClient.invalidateQueries({ queryKey: ['documents-view', projectId] })
      message.success('Document reclassé.')
    },
    onError: (error) => {
      console.error('Error reclassifying a shared document:', error)
      message.error('Le document n’a pas pu être reclassé.')
    },
  })

  return {
    documents: query.data,
    isLoading: query.isLoading,
    refresh,
    // The mutation says what happened; the row only needs to know it is over.
    removeDocument: (fileId: string) => remove.mutateAsync(fileId).catch(() => undefined),
    reclassifyDocument: (fileId: string, usage: string) => reclassify.mutate({ fileId, usage }),
  }
}
